<template>
    <div class="p-6 bg-gray-50 min-h-screen">
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions['api_key_add'])"
                color="info"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                @click="createApiKey">
                {{ $t('labels.add') }}
            </hts-form-button>
        </div>

        <hts-data-table
            ref="dataTables"
            store-name="connectDataTable"
            store-key="Connect"
            :use-archive="useArchive"
            use-selection
            :headers="headers"
            :server="api" />

        <addApiKey
            v-if="checkPermission(layoutPermissions['api_key_add'])"
            ref="addApiKey"
            @refresh-tables="refreshTables()" />

        <editApiKey
            v-if="checkPermission(layoutPermissions['api_key_edit'])"
            ref="editApiKey"
            @refresh-tables="refreshTables()" />

        <hts-modal
            v-if="checkPermission(layoutPermissions['api_key_delete'])"
            :show="showPopupDelete"
            title="Xóa Api key"
            type="confirm"
            content="Bạn có chắc muốn xoá Api key? Tất cả thông tin sẽ bị mất và không thể quay lại"
            @hide="showPopupDelete = false"
            @confirm="deleteApiKey" />
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import AddApiKey from './add.vue'
import EditApiKey from './edit.vue'
import TableKey from './table/key.vue'
import Actions from '~/pages/trucker-[id]/driver/ignore/actions.vue'
import layoutPermissions from '~/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
const api = $url.company.api_key.get

const dataTables = ref()
const addApiKey = ref()
const editApiKey = ref()
const showPopupDelete = ref()
const connectIsSelectedForDelete = ref('')

// Tạo API Key
const createApiKey = () => {
  addApiKey.value && addApiKey.value.open()
}

const handleEdit = (data: any) => {
  editApiKey.value && editApiKey.value.open(data._id, data)
}

const handleDelete = (data: any) => {
  showPopupDelete.value = true
  connectIsSelectedForDelete.value = data._id
}

// Sao chép API Key vào clipboard
const copyKey = (data: any) => {
  const key = data.key
  navigator.clipboard.writeText(key).then(() => {
    process.client && $toast().success('Api key đã được sao chép!')
  })
}

const headers = ref([
  {
    field: 'name',
    headerName: $t('labels.name'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'key',
    headerName: $t('labels.apiKey'),
    cellRenderer: TableKey,
    minWidth: 500
  },
  {
    field: 'allowedIps',
    headerName: $t('labels.allowedIps'),
    valueFormatter: (params: any) => {
      const { allowedIps } = params?.data || {}

      if (!allowedIps || !allowedIps.length) {
        return ''
      }

      return allowedIps.join(', ')
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    popup: true,
    suppressRowTransform: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'connectDataTable',
      storeName: 'Connect',
      items: [
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.api_key_edit),
          click: (data: any) => handleEdit(data)
        },
        {
          icon: 'hi-clipboard-document-icon',
          label: $t('labels.copy'),
          class: 'text-blue-600 hover:text-blue-500',
          isAll: true,
          permissions: checkPermission(layoutPermissions.api_key_management),
          click: (data: any) => copyKey(data)
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.api_key_delete),
          click: handleDelete,
          isArchive: true
        }
      ]
    }
  }
])

const useArchive = { api: $url.company.api_key.archive, permissions: checkPermission(layoutPermissions.api_key_archive) }

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

// Xóa API Key
const deleteApiKey = () => {
  const api = $url.company.api_key.delete

  $api(api, { body: { ids: [connectIsSelectedForDelete.value || ''] } }).then((data: any) => {
    const resData: any = data?.data?.value

    if (resData?.success) {
      connectIsSelectedForDelete.value = ''
      refreshTables()
      showPopupDelete.value = false
      process.client && $toast().success('Xoá Api key thành công')
    }
  })
}
</script>
